<template>
  <!-- Backdrop -->
  <div class="mobile-sidebar-backdrop" :class="{ open: isOpen }" @click="handleClose"></div>
  
  <!-- Sidebar -->
  <div class="mobile-sidebar" :class="{ open: isOpen }" v-click-outside="handleClose">
    <div class="mobile-sidebar__container">
      <!-- Business Selection View -->
      <Mob_BusinessSelection 
        v-if="showBusinessSelection" 
        @back="showBusinessSelection = false"
      />
      
      <!-- Main Menu View -->
      <div v-else>
      <div class="mob-sidebar-user">
        <div 
          v-if="currentUser?.image" 
          class="mob-sidebar-user__img" 
          :style="{ backgroundImage: `url(${avatarUrl})` }"
        ></div>

        <div class="mob-sidebar-user__name">{{ currentUser?.name }}</div>
      </div>

    <div class="mob-sidebar-menu">
      <div v-for="item in menuItems" :key="item.key">
        <!-- Hide Switch Business if user has only one business -->
        <div v-if="item.key === 'business-selection' && !hasMultipleBusinesses" style="display: none;"></div>
        <div v-else>
          <div class="mob-sidebar-menu__item" :class="{ active: isActiveMenuItem(item) }" @click="toggleMenuItem(item.key)">
            <div class="mob-sidebar-menu__icon">
              <SvgIcon 
                :icon="item.icon" 
                :icon_width="'18'" 
                :icon_height="'18'"
                :viewBox="getIconViewBox(item.icon)"
              />
            </div>
            <div class="mob-sidebar-menu__label">{{ getTranslatedLabel(item.key, item.label) }}</div>
            <div v-if="item.subMenu" class="mob-sidebar-menu__arrow">
              <svg
                class="icon"
                :class="{ 'ic-arrow-right': !item.isOpen, 'ic-arrow-up': item.isOpen }"
                :width="arrowDimensions(item.isOpen).width"
                :height="arrowDimensions(item.isOpen).height"
              >
                <use
                  v-if="!item.isOpen"
                  xlink:href="@/assets/sprites/sprite.svg#ic-arrow-right"
                ></use>
                <use v-else xlink:href="@/assets/sprites/sprite.svg#ic-arrow-up"></use>
              </svg>
            </div>
          </div>
          <div v-if="item.subMenu" class="mob-sidebar-submenu" :class="{ open: item.isOpen }">
            <router-link
              v-for="subItem in item.subMenu"
              :key="subItem.key"
              :to="subItem.link"
              class="submenu-link"
              @click="handleClose"
            >
              <div class="mob-sidebar-submenu__item">
                <div class="mob-sidebar-submenu__icon">
                  <SvgIcon 
                    :icon="subItem.icon" 
                    :icon_width="'16'" 
                    :icon_height="'16'"
                    :viewBox="getIconViewBox(subItem.icon)"
                  />
                </div>
                <div class="mob-sidebar-submenu__label">{{ getTranslatedSubLabel(subItem.key, subItem.label) }}</div>
              </div>
            </router-link>
          </div>
        </div>
      </div>
      </div>

      <div class="mob-sidebar-bottom">
        <span class="mob-sidebar-btn">
          <svg class="icon ic-msg" width="21" height="20">
            <use xlink:href="@/assets/sprites/sprite.svg#ic-msg"></use>
          </svg>
          {{ trans('Support') }}
        </span>
        <div class="mob-sidebar-exit" @click.prevent="logoutUser">
          <div class="mob-sidebar-exit__icon">
            <svg class="icon ic-mob-logout" width="20" height="20">
              <use xlink:href="@/assets/sprites/sprite.svg#ic-mob-logout"></use>
            </svg>
          </div>
          <div class="mob-sidebar-exit__label">{{ trans('Logout') }}</div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { trans } from '@/common/language/translations';

import { computed, ref, watch } from 'vue';
import { useAuthStore } from '@/store/auth';
import { NO_AVATAR_URL } from '@/common/lib/avatar';
import { useRoute, useRouter } from 'vue-router';
import { useUIStore } from '@/store/ui';
import SvgIcon from '@/components/UI/SvgIcon.vue';
import Mob_BusinessSelection from './Mob_BusinessSelection.vue';

interface SubMenuItem {
  key: string;
  link: string;
  icon: string;
  label: string;
}

interface MenuItem {
  key: string;
  icon: string;
  label: string;
  iconWidth: number;
  iconHeight: number;
  isOpen: boolean;
  subMenu?: SubMenuItem[];
}

// Props
defineProps<{
  isOpen: boolean;
}>();

// Emits
const emit = defineEmits<{
  'close': [];
}>();

const router = useRouter();
const currentRoute = useRoute();
const uiStore = useUIStore();
const authStore = useAuthStore();
const currentUser = computed(() => authStore.getCurrentUser);
const currentBusiness = computed(() => authStore.getCurrentBusiness);
const avatarUrl = computed<string>(() => currentUser.value!.image ?? NO_AVATAR_URL);

// Check if user has multiple businesses
const hasMultipleBusinesses = computed(() => {
  return authStore.getBusinesses && authStore.getBusinesses.length > 1;
});

// State for active menu item
const activeMenuItem = ref<string>('me'); // Default to 'me'

// State for business selection view
const showBusinessSelection = ref<boolean>(false);

const menuItems = ref<MenuItem[]>([
  {
    key: 'My profile',
    icon: 'ic-user',
    label: 'My profile',
    iconWidth: 20,
    iconHeight: 20,
    isOpen: false,
    subMenu: [
      {
        key: 'profile',
        link: '/user/settings/profile',
        icon: 'ic-user',
        label: 'Profile'
      },
      {
        key: 'shifts',
        link: '/user/settings/shifts',
        icon: 'ic-shift',
        label: 'My Shifts'
      },
      {
        key: 'notifications',
        link: '/user/settings/notifications',
        icon: 'ic-notice',
        label: 'Notifications Settings'
      }
    ]
  },
  {
    key: 'business',
    icon: 'ic-briefcase',
    label: currentBusiness.value?.name || 'Business',
    iconWidth: 21,
    iconHeight: 19,
    isOpen: false,
    subMenu: [
      {
        key: 'business-info',
        link: `/b/${currentBusiness.value?._id}/settings/profile`,
        icon: 'ic-briefcase',
        label: 'Business Info'
      },
      {
        key: 'integrations',
        link: `/b/${currentBusiness.value?._id}/settings/integrations`,
        icon: 'ic-link',
        label: 'Integrations'
      },
      {
        key: 'leads-statuses',
        link: `/b/${currentBusiness.value?._id}/settings/leads-statuses`,
        icon: 'ic-note',
        label:  'Leads statuses'
      },
      {
        key: 'business-notifications',
        link: `/b/${currentBusiness.value?._id}/settings/notifications`,
        icon: 'ic-note',
        label:  'Notifications settings'
      },
      {
        key: 'api-keys',
        link: `/b/${currentBusiness.value?._id}/settings/api-keys`,
        icon: 'ic-key',
        label: 'API Keys'
      },
      {
        key: 'business-create',
        link: '/business/create',
        icon: 'ic-plus',
        label: 'Create business'
      }
    ]
  },
  {
    key: 'business-selection',
    icon: 'ic-briefcase',
    label: 'Switch Business',
    iconWidth: 21,
    iconHeight: 19,
    isOpen: false
  },
  {
    key: 'dashboard',
    icon: 'ic-dashboard',
    label: 'Dashboard',
    iconWidth: 18,
    iconHeight: 18,
    isOpen: false
  }
  // {
  //   key: 'projects',
  //   icon: 'ic-projects',
  //   label: 'Projects',
  //   iconWidth: 19,
  //   iconHeight: 19,
  //   isOpen: false
  // }
  // {
  //   key: 'calendar',
  //   icon: 'ic-calendar',
  //   label: 'Calendar',
  //   iconWidth: 18,
  //   iconHeight: 20,
  //   isOpen: false
  // },
  // {
  //   key: 'messenger',
  //   icon: 'ic-messenger',
  //   label: 'Messenger',
  //   iconWidth: 21,
  //   iconHeight: 19,
  //   isOpen: false
  // },
  // {
  //   key: 'info-portal',
  //   icon: 'ic-folder',
  //   label: 'Info Portal',
  //   iconWidth: 21,
  //   iconHeight: 16,
  //   isOpen: false
  // }
]);

function handleClose(): void {
  uiStore.toggleMobileSideBar(false);
  emit('close');
}

async function logoutUser(): Promise<void> {
  try {
    await authStore.logout();
    await router.push('/auth/login');
  } catch (error) {
    console.error(trans('Failed to logout'), error);
  }
}

function toggleMenuItem(key: string): void {
  // Set the clicked item as active
  activeMenuItem.value = key;
  
  // Find the clicked item
  const clickedItem = menuItems.value.find(item => item.key === key);
  
  // Handle business selection
  if (key === 'business-selection') {
    showBusinessSelection.value = true;
    return;
  }
  
  // If item has no submenu, navigate and close sidebar
  if (!clickedItem?.subMenu) {
    if (key === 'dashboard') {
      router.push('/dashboard');
      handleClose();
      return;
    }
  }
  
  // Toggle the submenu for the clicked item
  menuItems.value.forEach(item => {
    if (item.key === key) {
      item.isOpen = !item.isOpen;
    } else {
      item.isOpen = false;
    }
  });
}

function arrowDimensions(isOpen: boolean): { width: number; height: number } {
  return isOpen ? { width: 11, height: 6 } : { width: 6, height: 11 };
}

function isActiveMenuItem(item: MenuItem): boolean {
  return activeMenuItem.value === item.key;
}

function getTranslatedLabel(key: string, fallback: string): string {
  const translations: Record<string, string> = {
    'My profile': trans('My profile'),
    'business': currentBusiness.value?.name || trans('Business'),
    'business-selection': trans('Switch Business'),
    'dashboard': trans('Dashboard') // No translation available
  };
  return translations[key] || fallback;
}

function getTranslatedSubLabel(key: string, fallback: string): string {
  const translations: Record<string, string> = {
    'profile': trans('Profile'), // No translation available
    'shifts': trans('My shifts'),
    'leads-statuses': trans('Leads statuses'),
    'notifications': trans('Notifications settings'),
    'business-info': trans('Business Info'), // No translation available
    'integrations': trans('Integrations'),
    'business-notifications': trans('Notifications settings'),
    'api-keys': trans('API keys'),
    'business-create': trans('Create business')
  };
  return translations[key] || fallback;
}

function getIconViewBox(iconName: string): string {
  const viewBoxMap: Record<string, string> = {
    // Main menu icons
    'ic-briefcase': '0 0 15.3 15.7',
    'ic-dashboard': '0 0 18 18',
    'ic-user': '0 0 20 20',
    // Submenu icons
    'ic-shift': '0 0 24 24',
    'ic-notice': '0 0 18.3 20.5',
    'ic-link': '0 0 17.2 10',
    'ic-note': '0 0 17.2 17.2',
    'ic-key': '0 0 24 24',
    'ic-plus': '0 0 10.1 10.1'
  };
  return viewBoxMap[iconName] || '0 0 24 24';
}

watch(
  () => authStore.isLoggedIn,
  newVal => {
    authStore.setLoggedInState(newVal);
  },
  { immediate: true }
);

watch(currentRoute, () => {
  uiStore.toggleMobileSideBar(false);
});
</script>

